import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useLayoutEffect,
} from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, matchPath } from 'react-router-dom';
import FlipClock from './components/FlipClock';
import AirplaneAnimation from './components/AirplaneAnimation';
import WalletCards from './components/WalletCards';
import WalletCardsLegacy from './components/WalletCardsLegacy';
import AirplaneDetailModal from './components/AirplaneDetailModal';
import TopicPage from './pages/TopicPage';
import ScrollToTop from './components/ScrollToTop';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import ThemeSwitcher from './components/ThemeSwitcher';
import FeedbackButton from './components/FeedbackButton';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { useFreezeBackground } from './hooks/useFreezeBackground';
import {
  arePlaneIdListsEqual,
  normalizeComparisonIds,
} from './components/comparisonUtils';
import { fetchAirplanes, getCachedAirplanes } from './lib/contentCache';

function MainContent() {
  const heroTitle = 'Fly Insight';
  const cachedAirplanes = getCachedAirplanes();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [allPlanes, setAllPlanes] = useState(() => cachedAirplanes ?? []);
  const [filteredPlanes, setFilteredPlanes] = useState(() => cachedAirplanes ?? []);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(() => !cachedAirplanes);
  const [error, setError] = useState('');
  const [detailPlane, setDetailPlane] = useState(null);
  const [compareSelectionIds, setCompareSelectionIds] = useState([]);
  const [minHeight, setMinHeight] = useState(() => {
    if (typeof window === 'undefined') return null;
    const savedHeight = window.sessionStorage.getItem('homeScrollHeight');
    const parsed = Number(savedHeight);
    return Number.isFinite(parsed) ? parsed : null;
  });
  const [savedScroll] = useState(() => {
    if (typeof window === 'undefined') return null;
    const savedY = window.sessionStorage.getItem('homeScrollY');
    const savedHeight = window.sessionStorage.getItem('homeScrollHeight');
    const parsedY = Number(savedY);
    const parsedHeight = Number(savedHeight);
    if (!Number.isFinite(parsedY)) return null;
    return {
      y: parsedY,
      height: Number.isFinite(parsedHeight) ? parsedHeight : null,
    };
  });
  const [curiosityReady, setCuriosityReady] = useState(false);

  const cardsRef = useRef(null);
  const location = useLocation();
  const restoreDoneRef = useRef(false);
  const restoreInProgressRef = useRef(false);
  const userScrolledRef = useRef(false);
  const userScrollIntentRef = useRef(false);
  const isDetailOpen = Boolean(detailPlane);

  useFreezeBackground(isDetailOpen);

  const clearHomeScroll = useCallback(() => {
    sessionStorage.removeItem('homeScrollY');
    sessionStorage.removeItem('homeScrollHeight');
    sessionStorage.removeItem('homeScrollLock');
    sessionStorage.removeItem('homeScrollRestore');
  }, []);

  const performRestore = useCallback(
    (finalize = false) => {
      if (!savedScroll || restoreDoneRef.current) return;
      const targetY = savedScroll.y;
      const needsScroll = Math.abs(window.scrollY - targetY) > 1;
      const previousBehavior = document.documentElement.style.scrollBehavior;
      restoreInProgressRef.current = true;
      document.documentElement.style.scrollBehavior = 'auto';
      if (needsScroll) {
        window.scrollTo(0, targetY);
      }
      document.documentElement.style.scrollBehavior = previousBehavior;
      window.requestAnimationFrame(() => {
        restoreInProgressRef.current = false;
      });

      if (finalize) {
        clearHomeScroll();
        restoreDoneRef.current = true;
      }
    },
    [clearHomeScroll, savedScroll]
  );

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (sessionStorage.getItem('homeScrollLock') === '1') {
        return;
      }
      sessionStorage.setItem('homeScrollY', String(window.scrollY));
      sessionStorage.setItem('homeScrollHeight', String(document.documentElement.scrollHeight));
    };
  }, []);

  useEffect(() => {
    const markUserIntent = () => {
      userScrollIntentRef.current = true;
    };

    const scrollKeys = new Set([
      'ArrowUp',
      'ArrowDown',
      'PageUp',
      'PageDown',
      'Home',
      'End',
      ' ',
    ]);

    const handleKeyDown = (event) => {
      if (scrollKeys.has(event.key)) {
        markUserIntent();
      }
    };

    window.addEventListener('wheel', markUserIntent, { passive: true });
    window.addEventListener('touchstart', markUserIntent, { passive: true });
    window.addEventListener('touchmove', markUserIntent, { passive: true });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', markUserIntent);
      window.removeEventListener('touchstart', markUserIntent);
      window.removeEventListener('touchmove', markUserIntent);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (restoreInProgressRef.current) return;
      if (!userScrollIntentRef.current) return;
      userScrolledRef.current = true;
      userScrollIntentRef.current = false;
      if (!restoreDoneRef.current && savedScroll) {
        clearHomeScroll();
        restoreDoneRef.current = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [clearHomeScroll, savedScroll]);

  useEffect(() => {
    let isMounted = true;

    fetchAirplanes()
      .then((planes) => {
        if (!isMounted) return;
        setAllPlanes(planes);
        setError('');
      })
      .catch((e) => {
        if (!isMounted) return;
        setError(e?.message || 'Failed to load data.');
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const lowercasedFilter = searchTerm.toLowerCase().replace(/\s/g, '');
    const filteredData = allPlanes.filter((item) => {
      const nameValue = item?.name ? String(item.name) : '';
      return nameValue.toLowerCase().replace(/\s/g, '').includes(lowercasedFilter);
    });
    setFilteredPlanes(filteredData);
  }, [searchTerm, allPlanes]);

  useEffect(() => {
    setCompareSelectionIds((currentIds) => {
      const nextIds = normalizeComparisonIds(currentIds, allPlanes);
      return arePlaneIdListsEqual(currentIds, nextIds) ? currentIds : nextIds;
    });
  }, [allPlanes]);

  const handleCardClick = (plane, index) => {
    setDetailPlane({ plane, index });
  };

  const handleCloseDetailModal = () => {
    setDetailPlane(null);
  };

  const handleNavigate = (direction) => {
    if (!detailPlane) return;
    const { index } = detailPlane;
    const newIndex = (index + direction + filteredPlanes.length) % filteredPlanes.length;
    setDetailPlane({ plane: filteredPlanes[newIndex], index: newIndex });
  };

  useEffect(() => {
    if (location.hash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.hash]);

  useLayoutEffect(() => {
    if (!savedScroll || restoreDoneRef.current) return;
    performRestore(false);
  }, [performRestore, savedScroll]);

  useLayoutEffect(() => {
    if (!savedScroll || restoreDoneRef.current) return;
    if (loading || !curiosityReady || userScrolledRef.current) return;
    performRestore(true);
  }, [loading, curiosityReady, performRestore, savedScroll]);

  useEffect(() => {
    if (minHeight === null) return;
    if (loading) return;
    const actualHeight = document.documentElement.scrollHeight;
    if (actualHeight >= minHeight) {
      setMinHeight(null);
      return;
    }
    const rafId = window.requestAnimationFrame(() => {
      const updatedHeight = document.documentElement.scrollHeight;
      if (updatedHeight >= minHeight) {
        setMinHeight(null);
      }
    });
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [loading, minHeight, filteredPlanes.length]);

  const hasActiveSearch = searchTerm.trim().length > 0;
  const showEmptySearch = !loading && !error && hasActiveSearch && filteredPlanes.length === 0;
  const suppressWalletLoadingStatus = Boolean(savedScroll);

  return (
    <div
      className="relative flex w-full flex-col items-center px-6 pb-24 pt-28"
      style={{
        minHeight: minHeight ? `${minHeight}px` : undefined,
      }}
    >
      <header className="flex w-full max-w-5xl flex-col items-center gap-6 text-center">
        <Badge
          variant="secondary"
          className="rounded-full bg-card/70 text-[11px] uppercase tracking-[0.4em] text-foreground shadow-md backdrop-blur"
        >
          Aviation Codex
        </Badge>
        <div className="space-y-3">
          <h1 className="cloud-title-wrap text-4xl sm:text-6xl" aria-label={heroTitle}>
            <span className="cloud-title" aria-hidden="true">
              {Array.from(heroTitle).map((char, index) =>
                char === ' ' ? (
                  <span key={`gap-${index}`} className="cloud-title-gap" />
                ) : (
                  <span
                    key={`${char}-${index}`}
                    className="cloud-title-letter"
                    data-letter={char}
                    style={{ '--letter-index': index }}
                  >
                    <span className="cloud-title-letter-core" data-letter={char}>
                      {char}
                    </span>
                  </span>
                )
              )}
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base">
            Curated aircraft intelligence, performance highlights, and story-driven details.
          </p>
        </div>
        <div className="flex w-full max-w-md items-center justify-center px-4 py-3 sm:px-6">
          <div className="scale-90 sm:scale-100">
            <FlipClock time={currentTime} />
          </div>
        </div>
      </header>

      <div className="mt-16 flex w-full flex-col items-center gap-10">
        <div className="flex w-full items-center justify-center translate-y-[50%]">
          <AirplaneAnimation />
        </div>

        <div className="flex w-full flex-col items-center translate-y-[5%]">
          <div
            className="flex w-full max-w-2xl flex-col items-center gap-4"
            ref={cardsRef}
            style={{ transform: "translateY(97%)" }}
          >
            <div className="search-glow w-full rounded-full bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-400 p-[1.5px] shadow-glow">
              <Input
                type="text"
                placeholder="Search by name, manufacturer, or model..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-12 rounded-full bg-background/70 px-5 font-mono text-sm shadow-inner backdrop-blur focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label="Search airplanes"
              />
            </div>
          </div>

          <section className="mt-16 w-full max-w-6xl">
            {showEmptySearch && (
              <div className="mb-6 rounded-2xl border border-border/60 bg-card/70 px-6 py-4 text-center text-sm text-muted-foreground shadow-sm backdrop-blur">
                暂无该机型资料，换个关键词试试吧。
              </div>
            )}
            <WalletCardsLegacy
              planes={filteredPlanes}
              loading={loading}
              error={error}
              onCardClick={handleCardClick}
              onCuriosityReady={() => {
                setCuriosityReady(true);
              }}
              scrollTo={location.state?.scrollTo}
              suppressLoadingStatus={suppressWalletLoadingStatus}
              comparisonPlanes={allPlanes}
              comparisonLoading={loading}
              comparisonSelectionIds={compareSelectionIds}
              onComparisonSelectionChange={setCompareSelectionIds}
            />
          </section>
        </div>
      </div>

      <AirplaneDetailModal
        detailData={detailPlane}
        onClose={handleCloseDetailModal}
        onNavigate={handleNavigate}
        canNavigate={filteredPlanes.length > 1}
      />
    </div>
  );
}

function Layout({ children }) {
  const { theme } = useContext(ThemeContext);
  const backgroundRef = useRef(null);
  const freezeRef = useRef(false);

  useEffect(() => {
    const backgroundEl = backgroundRef.current;
    if (!backgroundEl) return;

    const updateOffset = (scrollY) => {
      backgroundEl.style.setProperty('--bg-offset', `${-scrollY / 2}px`);
    };

    const handleScroll = () => {
      const html = document.documentElement;
      const isFrozen = html.getAttribute('data-freeze-bg') === '1';
      freezeRef.current = isFrozen;
      if (isFrozen) return;
      updateOffset(window.scrollY || window.pageYOffset || 0);
    };

    const observer = new MutationObserver(() => {
      const html = document.documentElement;
      const isFrozen = html.getAttribute('data-freeze-bg') === '1';
      if (isFrozen) {
        freezeRef.current = true;
        const frozenY = Number(html.getAttribute('data-freeze-bg-y'));
        if (Number.isFinite(frozenY)) {
          updateOffset(frozenY);
        }
      } else {
        freezeRef.current = false;
        handleScroll();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-freeze-bg', 'data-freeze-bg-y'],
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const backgroundStyle = {
    backgroundImage: theme.backgroundImage,
    backgroundSize: 'cover',
    backgroundPosition: 'center var(--bg-offset, 0px)',
    backgroundRepeat: 'no-repeat',
    opacity: theme.name === 'light' ? 0.45 : 0.6,
    filter: theme.name === 'dark' ? 'saturate(1.2) brightness(0.75)' : 'saturate(1.05)',
  };

  return (
    <div className="relative min-h-screen w-full">
      <div className="pointer-events-none fixed inset-0 -z-20 overflow-hidden">
        <div className="absolute -top-24 right-[-10%] h-72 w-72 rounded-full bg-primary/25 blur-3xl animate-float" />
        <div className="absolute bottom-[-20%] left-[-5%] h-80 w-80 rounded-full bg-accent/20 blur-3xl animate-drift" />
      </div>
      <div
        ref={backgroundRef}
        className="pointer-events-none fixed inset-0 -z-10 transition-[background-image,filter] duration-500"
        style={backgroundStyle}
        aria-hidden="true"
      />
      <div className="relative z-10 flex min-h-screen w-full flex-col items-center text-foreground">
        <ThemeSwitcher />
        <FeedbackButton />
        {children}
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <Layout>
          <AppRoutes />
        </Layout>
      </Router>
    </ThemeProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isTopicRoute = Boolean(matchPath('/topics/:topicId', location.pathname));
  const backgroundLocation = location.state?.backgroundLocation;
  const routesLocation =
    backgroundLocation ||
    (isTopicRoute
      ? {
          ...location,
          pathname: '/',
          search: '',
          hash: '',
          state: null,
          key: `${location.key}-topic-background`,
        }
      : location);

  return (
    <>
      <Routes location={routesLocation}>
        <Route path="/" element={<MainContent />} />
        <Route path="/wallet" element={<WalletCards />} />
      </Routes>
      {isTopicRoute ? (
        <Routes>
          <Route path="/topics/:topicId" element={<TopicPage isOverlay />} />
        </Routes>
      ) : null}
    </>
  );
}

export default App;
